import { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataset,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import colors from "../../../../theme/Colours";
import {
  SalesAnalyticsCardLabel,
  SalesAnalyticsContentBox,
  SalesAnalyticsOverflowBox,
  StyledSalesAnalyticsCard,
} from "../styles";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

type TrendPoint = {
  freq: string;
  revenue?: string | number;
  vol?: string | number;
  weekStartDate?: string;
  weekEndDate?: string;
};

type PeriodData = {
  period: "current" | "one" | "two" | string;
  name: string;
  frequency?: string;
  values: TrendPoint[];
};

export default function SalesCollectionTrend({
  comparisonFilterEnabled,
  data,
  periodValues,
  label = "Sales Revenue Trend",
}: {
  comparisonFilterEnabled?: boolean;
  data: {
    key: string;
    name: string;
    unit: string;
    details: PeriodData[];
  };
  periodValues: any;
  label?: string;
}) {
  const periodData = useMemo(() => {
    const details = data?.details || [];
    const period1 = details.find((d) => d.period === "one")?.values || [];
    const period2 = details.find((d) => d.period === "two")?.values || [];
    const current = details.find((d) => d.period === "current")?.values || [];

    const createMap = (arr: TrendPoint[]) => ({
      vol: new Map(arr.map((p) => [p.freq, Number(p.revenue || p.vol) || 0])),
      freqs: arr.map((p) => p.freq),
      weekDates: new Map(
        arr.map((p) => [
          p.freq,
          {
            startDate: p.weekStartDate,
            endDate: p.weekEndDate,
          },
        ])
      ),
    });

    return {
      period1: createMap(period1),
      period2: createMap(period2),
      current: createMap(current),
    };
  }, [data]);

  const labels = useMemo(() => {
    if (!comparisonFilterEnabled) {
      // Create labels with dates for current period
      return periodData.current.freqs.map((freq) => {
        const weekDates = periodData.current.weekDates.get(freq);
        if (weekDates?.startDate && weekDates?.endDate) {
          const formatDate = (dateStr: string) => {
            const date = new Date(dateStr);
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = String(date.getFullYear()).slice(-2);
            return `${day}/${month}/${year}`;
          };
          return `${freq}\n(${formatDate(weekDates.startDate)} - ${formatDate(weekDates.endDate)})`;
        }
        return freq;
      });
    }

    // When compare periods is enabled, use Period 1 and Period 2 with date ranges
    return [
      periodValues?.["Period 1"] || "Period 1",
      periodValues?.["Period 2"] || "Period 2",
    ];
  }, [comparisonFilterEnabled, periodData, periodValues]);

  const chartData = useMemo(() => {
    const commonBarStyle: Partial<ChartDataset<"bar", number[]>> = {};

    if (comparisonFilterEnabled) {
      const { period1, period2 } = periodData;
      // When compare periods is enabled, we show aggregated data for Period 1 and Period 2
      const period1Total = Array.from(period1.vol.values()).reduce(
        (sum, val) => sum + val,
        0
      );
      const period2Total = Array.from(period2.vol.values()).reduce(
        (sum, val) => sum + val,
        0
      );

      return {
        labels,
        datasets: [
          {
            label: "Sales Revenue",
            data: [period1Total, period2Total],
            backgroundColor: [colors.linePlotBlue, colors.linePlotOrange],
            borderColor: [colors.linePlotBlue, colors.linePlotOrange],
            borderWidth: 1,
          },
        ],
      };
    }

    return {
      labels,
      datasets: [
        {
          label: "Current Collection",
          data: periodData.current.freqs.map(
            (f) => periodData.current.vol.get(f) ?? 0
          ),
          borderColor: colors.linePlotBlue,
          backgroundColor: colors.linePlotBlue,
          ...commonBarStyle,
        },
      ],
    };
  }, [comparisonFilterEnabled, labels, periodData]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },

        tooltip: {
          backgroundColor: colors.background2,
          titleColor: colors.salesAnalyticsCardLabelText,
          bodyColor: colors.salesAnalyticsCardLabelText,
          borderColor: colors.greyBorder,
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          callbacks: {
            title: () => "",
            label: (context: any) => {
              const value = context.parsed.y;
              const numValue = Number(value);
              if (numValue >= 10000000)
                return `₹ ${(numValue / 10000000).toFixed(1)}Cr`;
              if (numValue >= 100000)
                return `₹ ${(numValue / 100000).toFixed(1)}L`;
              if (numValue >= 1000) return `₹ ${(numValue / 1000).toFixed(1)}K`;
              return `₹ ${numValue}`;
            },
          },
        },
      },

      datasets: {
        bar: {
          barPercentage: comparisonFilterEnabled ? 0.4 : 0.8,
          categoryPercentage: comparisonFilterEnabled ? 0.8 : 0.8,
        },
      },

      scales: {
        x: {
          grid: { color: colors.greyBorder, lineWidth: 1, drawBorder: false },
          border: { color: colors.greyBorder },
          ticks: {
            color: colors.salesAnalyticsCardLabelText,
            font: { size: 9 },
            maxRotation: 0,
            minRotation: 0,
            autoSkip: false,
            callback: function (value: any) {
              const label = labels[value];
              if (!label) return value;

              if (comparisonFilterEnabled) {
                // Center align period labels when compare periods is enabled
                const lines = label.split("\n");
                return lines.length > 1 ? lines : label;
              }

              return label.split("\n");
            },
          },
        },

        y: {
          beginAtZero: true,
          grid: { color: colors.greyBorder, lineWidth: 1, drawBorder: false },
          border: { color: colors.greyBorder },
          ticks: {
            color: colors.salesAnalyticsCardLabelText,
            font: { size: 11 },
            callback: (value: any) => {
              const numValue = Number(value);
              if (numValue >= 10000000)
                return `₹ ${(numValue / 10000000).toFixed(1)}Cr`;
              if (numValue >= 100000)
                return `₹ ${(numValue / 100000).toFixed(1)}L`;
              if (numValue >= 1000) return `₹ ${(numValue / 1000).toFixed(1)}K`;
              return `₹ ${numValue}`;
            },
          },
          title: {
            display: true,
            text: "Revenue (₹)",
            color: colors.salesAnalyticsCardHelperText,
            font: { size: 12 },
          },
        },
      },

      interaction: { intersect: true, mode: "nearest" as const },
    }),
    [comparisonFilterEnabled, periodValues]
  );

  return (
    <StyledSalesAnalyticsCard>
      <SalesAnalyticsCardLabel>{label}</SalesAnalyticsCardLabel>
      <SalesAnalyticsContentBox>
        <SalesAnalyticsOverflowBox>
          <Bar data={chartData} options={options} />
        </SalesAnalyticsOverflowBox>
      </SalesAnalyticsContentBox>
    </StyledSalesAnalyticsCard>
  );
}
